import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { userSelector } from '@src/selectors/app'
import { useLazyScreenPreload } from '@src/navigation/utils'
import {
  EhrAppointmentCustomType,
  getAppointmentDetails,
  isAppointmentScheduled,
  LatestEhrAppointmentCustomType,
} from '../../utils'
import { useCallSchedulingState } from './useCallSchedulingState'
import {
  LoadingCard,
  InitialCard,
  NoCallCard,
  UpcomingCallCard,
  PendingCallCard,
  UhcNoCallsLeftCard,
} from './Cards'

export interface NextCallCardProps {
  appointment: EhrAppointmentCustomType | LatestEhrAppointmentCustomType | null
  loading: boolean
}

export const NextCallCard = ({ appointment, loading }: NextCallCardProps) => {
  const navigation = useNavigation()
  const user = useSelector(userSelector)
  const { promptForInsurance, canSchedule, schedulingInfo } = useCallSchedulingState({ skip: true })
  useLazyScreenPreload('ScheduleNextCallModal')

  const appointmentDetails = appointment ? getAppointmentDetails(appointment) : undefined

  if (loading || !schedulingInfo) {
    return <LoadingCard showButton />
  }

  const scheduleNextCall = () => {
    navigation.navigate('ScheduleNextCallModal')
  }

  if (appointment && appointmentDetails && isAppointmentScheduled(appointmentDetails)) {
    return <UpcomingCallCard appointment={appointment} onButtonPress={scheduleNextCall} />
  }

  if (schedulingInfo.mnt?.pendingAppointment) {
    const pendingAppointment = schedulingInfo.mnt.pendingAppointment

    // canSchedule will be `false` for users that are only allowed to book pre-purchased calls, i.e. UHC members.
    return (
      <PendingCallCard
        pendingAppointment={pendingAppointment}
        chargeKind={schedulingInfo.mnt.chargeKind}
        onScheduleNextCall={canSchedule ? scheduleNextCall : undefined}
      />
    )
  }

  if (!canSchedule && !!user?.uhcMember) {
    return <UhcNoCallsLeftCard />
  }

  if (promptForInsurance) {
    return <InitialCard onButtonPress={scheduleNextCall} />
  }

  return <NoCallCard onButtonPress={scheduleNextCall} />
}
